import type { InteractionEvent } from "../types";

/**
 * Scheduled times — a show's start, a flight's departure and boarding — and
 * how the day went against them.
 *
 * They're stored as a bare clock time ("14:05", from an `<input type="time">`)
 * with no date, because that's how they're read off a board or a boarding
 * pass. Turning one into an instant needs a day and a zone: the day of the
 * activity's first event, read on the clock the activity was logged on.
 */

type Zone = string | undefined;

const DAY_S = 24 * 60 * 60;

/** Parse "HH:MM" (or "HH:MM:SS"); null when it isn't a clock time. */
export function parseClockTime(value: unknown): { h: number; m: number } | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return { h, m };
}

/** Seconds since local midnight of `instant` on `zone`'s clock. */
function secondsIntoDay(instant: Date, zone: Zone): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    timeZone: zone,
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return get("hour") * 3600 + get("minute") * 60 + get("second");
}

/**
 * The instant a clock time names, taken as the occurrence nearest `anchor`
 * (within 12 hours either side). So a 12:30 AM showtime anchored on an 11:50 PM
 * arrival lands after midnight, not 23 hours earlier.
 */
export function scheduledInstant(value: unknown, anchor: Date, zone: Zone): Date | null {
  const clock = parseClockTime(value);
  if (!clock) return null;
  let delta = clock.h * 3600 + clock.m * 60 - secondsIntoDay(anchor, zone);
  while (delta > DAY_S / 2) delta -= DAY_S;
  while (delta <= -DAY_S / 2) delta += DAY_S;
  return new Date(anchor.getTime() + delta * 1000);
}

function sortedEvents(events: InteractionEvent[] | undefined) {
  return (events ?? [])
    .map((e) => ({ type: e.eventType, label: e.customLabel ?? null, ms: e.eventTimestamp?.toDate?.()?.getTime() }))
    .filter(
      (e): e is { type: InteractionEvent["eventType"]; label: string | null; ms: number } =>
        e.ms != null && !Number.isNaN(e.ms),
    )
    .sort((a, b) => a.ms - b.ms);
}

const secondsBetween = (fromMs: number, toMs: number) => Math.round((toMs - fromMs) / 1000);

export interface ShowTiming {
  /** Seconds from the first logged event to the showtime (negative: arrived after it). */
  earlyBy: number | null;
  /** Seconds from the showtime to Show Started (negative: started early). */
  startedLate: number | null;
}

/** How a show went against its scheduled start. */
export function showTiming(
  events: InteractionEvent[] | undefined,
  scheduledTime: unknown,
  zone: Zone,
): ShowTiming {
  const sorted = sortedEvents(events);
  const first = sorted[0];
  if (!first) return { earlyBy: null, startedLate: null };
  const showtime = scheduledInstant(scheduledTime, new Date(first.ms), zone);
  if (!showtime) return { earlyBy: null, startedLate: null };
  const started = sorted.find((e) => e.type === "MAIN_EVENT_START");
  return {
    // Logging Show Started first says nothing about when you got there.
    earlyBy: first.type === "MAIN_EVENT_START" ? null : secondsBetween(first.ms, showtime.getTime()),
    startedLate: started ? secondsBetween(showtime.getTime(), started.ms) : null,
  };
}

export interface FlightTiming {
  /** Seconds from the first logged event to boarding time (negative: arrived after it). */
  earlyBy: number | null;
  /** Seconds from boarding time to the Boarding step (negative: boarded early). */
  boardingLate: number | null;
  /** Seconds from departure time to the Departed step (negative: left early). */
  departedLate: number | null;
}

const DEPARTURE_LABEL = /push\s*-?\s*back|depart|take\s*-?\s*off/i;

/** How a flight went against its scheduled boarding and departure. */
export function flightTiming(
  events: InteractionEvent[] | undefined,
  departureTime: unknown,
  boardingTime: unknown,
  zone: Zone,
): FlightTiming {
  const sorted = sortedEvents(events);
  const first = sorted[0];
  if (!first) return { earlyBy: null, boardingLate: null, departedLate: null };
  const anchor = new Date(first.ms);
  const boardsAt = scheduledInstant(boardingTime, anchor, zone);
  const departsAt = scheduledInstant(departureTime, anchor, zone);
  const boarded = sorted.find((e) => e.type === "BOARDING");
  // Places set up before Departed existed log leaving the gate as their own
  // "Push Back" step, saved as an activity start; fall back to that. An
  // unnamed activity start after boarding counts too, but a named one is
  // whatever its name says ("Seated", "Doors Closed"), not the departure.
  const departed =
    sorted.find((e) => e.type === "DEPARTED") ??
    sorted.find((e) => e.label != null && DEPARTURE_LABEL.test(e.label)) ??
    sorted.find((e) => e.type === "MAIN_EVENT_START" && !e.label?.trim() && (!boarded || e.ms >= boarded.ms));
  const arrivedBefore = first.type !== "BOARDING" && first !== departed;
  return {
    earlyBy: boardsAt && arrivedBefore ? secondsBetween(first.ms, boardsAt.getTime()) : null,
    boardingLate: boardsAt && boarded ? secondsBetween(boardsAt.getTime(), boarded.ms) : null,
    departedLate: departsAt && departed ? secondsBetween(departsAt.getTime(), departed.ms) : null,
  };
}

/**
 * "5m late", "2m early", "on time" (within a minute). For a lateness in
 * seconds, where negative means early.
 */
export function formatLateness(seconds: number, format: (s: number) => string): string {
  if (Math.abs(seconds) < 60) return "on time";
  return seconds > 0 ? `${format(seconds)} late` : `${format(-seconds)} early`;
}

/**
 * Showtimes from the park's feed worth offering as the scheduled time: those
 * on the same day as `around` (or now), as "HH:MM" on the activity's clock,
 * soonest first after it, each listed once.
 */
export function showtimeChoices(
  showtimes: { startTime?: string }[],
  around: Date,
  zone: Zone,
): string[] {
  const dayKey = (d: Date) =>
    new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: zone }).format(d);
  const clock = (d: Date) =>
    new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: zone }).format(d);
  const today = dayKey(around);
  const upcoming: { ms: number; value: string }[] = [];
  const earlier: { ms: number; value: string }[] = [];
  for (const s of showtimes) {
    const ms = s.startTime ? Date.parse(s.startTime) : NaN;
    if (Number.isNaN(ms)) continue;
    const d = new Date(ms);
    if (dayKey(d) !== today) continue;
    (ms >= around.getTime() - 15 * 60 * 1000 ? upcoming : earlier).push({ ms, value: clock(d) });
  }
  upcoming.sort((a, b) => a.ms - b.ms);
  earlier.sort((a, b) => b.ms - a.ms);
  return [...new Set([...upcoming, ...earlier].map((s) => s.value))];
}

/** A stored clock time ("14:30") as people read it ("2:30 PM"); anything unparseable is shown as-is. */
export function formatClockTime(value: unknown): string {
  const t = parseClockTime(value);
  if (!t) return String(value);
  return new Date(2000, 0, 1, t.h, t.m).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
